import { push } from "react-router-redux";
import { formValueSelector, SubmissionError } from "redux-form";
import { fromJS } from "immutable";
import api from "APP_ROOT/api";
import { SUCCESS } from "LIB/submission/constants";
import { ADD, ID, UUID } from "APP_ROOT/constants";
import { PORTAL_PURCHASE } from "MODULES/PurchaseOrders/constants";
import { toastr } from "react-redux-toastr";
import {
  incomingQuotePagination,
  expireLineItems
} from "MODULES/IncomingQuotes/pagination";
import * as actionTypes from "./actionTypes";

export function setIncomingQuoteWithAllLineItems(
  incomingQuoteWithAllLineItems
) {
  return {
    type: actionTypes.SET_INCOMING_QUOTE_WITH_ALL_LINE_ITEMS,
    incomingQuoteWithAllLineItems
  };
}

export function show(id, type) {
  return dispatch =>
    api.incomingQuotes.show(id, type).then(resp => {
      dispatch({
        type: actionTypes.SET,
        incomingQuote: resp.data
      });
      if (!type) {
        dispatch(setIncomingQuoteWithAllLineItems(resp.data));
      }
      return SUCCESS;
    });
}

export function fetchIncomingQuoteWithAllLineItems(id) {
  return dispatch =>
    api.incomingQuotes.show(id).then(({ data }) => {
      dispatch(setIncomingQuoteWithAllLineItems(data));
      return SUCCESS;
    });
}

export function getCompany(id) {
  return api.companies.show(id).then(resp => Promise.resolve(resp.data));
}

export function importLineItems(values) {
  return (dispatch, getState) => {
    const id = getState().incomingQuoteDetail.getIn(["incomingQuote", ID]);
    const fileName = values.file.name;

    return api.attachments.upload(values.file.name).then(resp => {
      const { key, url } = resp.data;
      const formattedAttachment = {
        name: fileName,
        key,
        quote_type: values.quote_type
      };

      return api.attachments
        .toS3(url, values.file)
        .then(() =>
          api.incomingQuotes.lineItems
            .import(id, formattedAttachment)
            .then(() => {
              show(id);
              toastr.info("Processing", "Importing Incoming Quote.");
              return SUCCESS;
            })
        )
        .catch(e => Promise.reject(e));
    });
  };
}

export function importOutgoingQuoteLineItems(values) {
  return (dispatch, getState) => {
    const id = getState().outgoingQuoteDetail.getIn(["outgoingQuote", ID]);
    const fileName = values.file.name;

    return api.attachments.upload(values.file.name).then(resp => {
      const { key, url } = resp.data;
      const formattedAttachment = {
        name: fileName,
        key,
        quote_type: values.quote_type
      };

      return api.attachments
        .toS3(url, values.file)
        .then(() =>
          api.outgoingQuotes.lineItems
            .import(id, formattedAttachment)
            .then(() => {
              show(id);
              toastr.info("Processing", "Importing Outgoing Quote.");
              return SUCCESS;
            })
        )
        .catch(e => Promise.reject(e));
    });
  };
}

export function updateIncomingQuote(data) {
  return (dispatch, getState) => {
    const quoteID = getState().incomingQuoteDetail.getIn(["incomingQuote", ID]);

    const uuid = getState().incomingQuoteDetail.getIn(["incomingQuote", UUID]);

    if (data.contact_id === ADD) {
      data.contact_id = null;
    }

    if (data.contact_id === PORTAL_PURCHASE) {
      data.contact_id = null;
      data.portal_purchase = true;
    }

    if (data.contact_id || (data.contact && data.contact.email)) {
      data.portal_purchase = false;
    }

    return api.incomingQuotes.update(quoteID, data).then(() => {
      dispatch(show(uuid, "no_lines"));
      dispatch(incomingQuotePagination.pageActions.expire());
      return SUCCESS;
    });
  };
}

export function convertToOutgoingQuote(id, params) {
  return dispatch => {
    if (params.contact_id === ADD) {
      params.contact_id = null;
    }
    return api.incomingQuotes.lineItems
      .convertToOutgoingQuote(id, params)
      .then(({ data }) => {
        dispatch(push(`/outgoing-quotes/${data.uuid}`));
      });
  };
}

export function createIncomingQuoteLineItem(data) {
  return (dispatch, getState) => {
    const quoteID = getState().incomingQuoteDetail.getIn([
      "incomingQuote",
      "id"
    ]);

    const uuid = getState().incomingQuoteDetail.getIn(["incomingQuote", UUID]);

    return api.incomingQuotes.lineItems.create(quoteID, data).then(() => {
      dispatch(show(uuid));
      dispatch(fetchIncomingQuoteWithAllLineItems(uuid));
      dispatch(expireLineItems());
      return SUCCESS;
    });
  };
}

export function deleteIncomingQuote() {
  return (dispatch, getState) => {
    const quoteID = getState().incomingQuoteDetail.getIn([
      "incomingQuote",
      "id"
    ]);

    return api.incomingQuotes.delete(quoteID).then(() => {
      dispatch(push("/incoming-quotes"));
      return SUCCESS;
    });
  };
}

export function updateIncomingQuoteLineItem(data, itemID) {
  return (dispatch, getState) => {
    const quoteID = getState().incomingQuoteDetail.getIn([
      "incomingQuote",
      "id"
    ]);

    return api.incomingQuotes.lineItems.update(itemID, data).then(() => {
      dispatch(show(quoteID));
      dispatch(fetchIncomingQuoteWithAllLineItems(quoteID));
      dispatch(expireLineItems());
      return SUCCESS;
    });
  };
}

export function deleteIncomingQuoteLineItem(id) {
  return (dispatch, getState) => {
    const quoteID = getState().incomingQuoteDetail.getIn([
      "incomingQuote",
      "id"
    ]);
    api.incomingQuotes.lineItems.delete(id).then(() => {
      dispatch(show(quoteID));
      dispatch(fetchIncomingQuoteWithAllLineItems(quoteID));
      dispatch(expireLineItems());
    });
  };
}

export function convertQuoteToPurchaseOrder(itemIds) {
  return (dispatch, getState) => {
    const quoteId = getState().incomingQuoteDetail.getIn([
      "incomingQuote",
      "id"
    ]);

    return api.incomingQuotes
      .convertToPurchaseOrder(quoteId, itemIds)
      .then(({ data }) => dispatch(push(`/purchase-orders/${data.uuid}`)))
      .catch(err => {
        // Its possible to submit a subset of the line items list, so we need to perform a small
        // transformation on the errors returned from server before handing them off to redux-form.
        // This ensures the server-side errors are properly matched up with their form fields.

        if (!err.errors.line_items || err.errors.line_items.length === 0) {
          throw new SubmissionError(err.errors);
        }

        // Clone the server-side error map so we can safely modify it by filling in any gaps between
        // error indexes with null values.
        const modifiedErrors = fromJS(err.errors).toJS();
        let includedLineItemxIdx = 0;

        formValueSelector("incomingQuoteConvertToPurchaseOrders")(
          getState(),
          "line_items"
        ).forEach((item, idx) => {
          if (item.included) {
            modifiedErrors.line_items[idx] =
              err.errors.line_items[includedLineItemxIdx];
            includedLineItemxIdx += 1;
          } else {
            modifiedErrors.line_items[idx] = null;
          }
        });

        throw new SubmissionError(modifiedErrors);
      });
  };
}



// WEBPACK FOOTER //
// ./app/modules/IncomingQuotes/actions.js